import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Forgot password modal state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);

  if (user && user.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotDone(true);
    } catch (_err) {
      // Always show the same message regardless to avoid enumeration
      setForgotDone(true);
    } finally {
      setForgotSubmitting(false);
    }
  };

  const closeForgot = () => {
    setForgotOpen(false);
    setForgotEmail("");
    setForgotDone(false);
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] text-[#F9F6F0] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-4">Webmaster</p>
        <h1 className="text-4xl lg:text-5xl font-light mb-10" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Sign in to manage Finca&nbsp;Cortesín.
        </h1>
        <form onSubmit={submit} className="space-y-6" data-testid="admin-login-form">
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-[#A8A39A] mb-2 block">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" className="w-full bg-transparent border-b border-[#333] focus:border-[#C5A880] py-3 text-[#F9F6F0] outline-none" autoFocus />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-[#A8A39A] mb-2 block">Password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" className="w-full bg-transparent border-b border-[#333] focus:border-[#C5A880] py-3 text-[#F9F6F0] outline-none" />
          </div>
          <button type="submit" disabled={submitting} data-testid="admin-login-submit" className="w-full bg-[#C5A880] text-[#1A1A1A] py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#F9F6F0] disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
            {submitting ? "Signing in" : "Sign In"}
          </button>
          <button
            type="button"
            onClick={() => setForgotOpen(true)}
            data-testid="admin-forgot-password"
            className="block w-full text-center text-[10px] tracking-[0.25em] uppercase text-[#A8A39A] hover:text-[#C5A880] transition-colors mt-4"
          >
            Forgot password?
          </button>
        </form>
      </div>

      {/* Forgot Password modal */}
      {forgotOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-6"
          onClick={closeForgot}
          data-testid="forgot-modal"
        >
          <div className="bg-[#1A1A1A] border border-[#333] w-full max-w-md p-10" onClick={(e) => e.stopPropagation()}>
            {!forgotDone ? (
              <form onSubmit={submitForgot}>
                <button
                  type="button"
                  onClick={closeForgot}
                  className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#A8A39A] hover:text-[#C5A880] mb-6"
                >
                  <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back
                </button>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-3">Webmaster</p>
                <h2 className="text-3xl font-light mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Reset password
                </h2>
                <p className="text-sm text-[#A8A39A] mb-6 leading-relaxed">
                  Enter your admin email. We'll send a reset link to your inbox. The link expires in 1 hour.
                </p>
                <label className="text-[10px] tracking-[0.25em] uppercase text-[#A8A39A] mb-2 block">Email</label>
                <input
                  type="email"
                  required
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  data-testid="forgot-email"
                  className="w-full bg-transparent border-b border-[#333] focus:border-[#C5A880] py-3 text-[#F9F6F0] outline-none mb-6"
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={forgotSubmitting}
                  data-testid="forgot-submit"
                  className="w-full bg-[#C5A880] text-[#1A1A1A] py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#F9F6F0] disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2"
                >
                  {forgotSubmitting && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
                  {forgotSubmitting ? "Sending" : "Send reset link"}
                </button>
              </form>
            ) : (
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-3">Webmaster</p>
                <h2 className="text-3xl font-light mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Check your inbox
                </h2>
                <p className="text-sm text-[#A8A39A] mb-8 leading-relaxed">
                  If an admin account exists for <strong className="text-[#F9F6F0]">{forgotEmail}</strong>, a password reset
                  link has been sent. It will expire in 1 hour.
                </p>
                <button
                  type="button"
                  onClick={closeForgot}
                  data-testid="forgot-done-close"
                  className="w-full border border-[#333] text-[#F9F6F0] py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#333] transition-colors"
                >
                  Back to sign in
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
